/**
 * Audit Context Builder for the Radar AI Advisor.
 *
 * Extracts the most advisor-relevant findings from all 13 tool results
 * into a structured text context for the system prompt. Also generates
 * dynamic suggested questions based on actual audit findings.
 */

import type { ToolKey, ToolState, Insight, ActionItem } from '@/types/radar'
import { hasUsableScore, hasUsableScoreData } from '@/lib/radar/tool-scores'
import { readAnswerEngineSources } from '@/lib/radar/answer-engine-result'
import { isCrawlRetrievalBot } from '@/lib/tools/crawl-bots'

/* eslint-disable @typescript-eslint/no-explicit-any */

const MAX_CONTEXT_LENGTH = 3000

/**
 * Build a structured audit context string from tool results.
 * Returns a text block with section headers that the LLM can reference.
 */
export function buildAuditContext(
  tools: Record<ToolKey, ToolState>,
  insights: Insight[],
  actions: ActionItem[],
  domain: string
): string {
  const sections: string[] = [`Domain: ${domain}`]

  // Unified score (if available from any tool data)
  const completedTools = Object.entries(tools).filter(
    ([, s]) => s.status === 'done' && s.data
  )
  if (completedTools.length === 0) return sections.join('\n')

  // Per-tool extraction
  const crawl = tools.crawl?.data
  const robots = tools.robots?.data
  const llms = tools.llms?.data
  const readiness = tools.readiness?.data
  const citation = tools.citation?.data
  const reddit = tools.reddit?.data
  const aeo = tools.aeo?.data
  const answer = tools.answer?.data
  const sourceInfluence = tools.sourceInfluence?.data
  const promptSov = tools.promptSov?.data
  const schemaAudit = tools.schemaAudit?.data
  const hallucinationCheck = tools.hallucinationCheck?.data

  // Tool scores summary
  const scores = completedTools
    .map(([key, s]) => {
      return !hasUsableScore(s)
        ? `${key}: withheld`
        : `${key}: ${s.data?.overallScore ?? '?'}/100`
    })
    .join(', ')
  sections.push(`Tool Scores: ${scores}`)

  // Crawl findings
  if (crawl) {
    const blockedAi =
      crawl.botAccess?.filter(
        (b: any) =>
          !b.allowed &&
          (b.category === 'ai-browse' || b.category === 'ai-train')
      ) || []
    const allowedCount =
      crawl.botAccess?.filter((b: any) => b.allowed).length || 0
    const totalBots = crawl.botAccess?.length || 0
    const lines = [`## Bot Access: ${allowedCount}/${totalBots} bots allowed`]
    if (blockedAi.length > 0) {
      lines.push(
        `Blocked AI bots: ${blockedAi.map((b: any) => `${b.bot} (${b.category})`).join(', ')}`
      )
    }
    if (crawl.structuredData)
      lines.push(
        `Structured data: ${crawl.structuredData.found ? 'found' : 'NOT found'}`
      )
    if (crawl.contentAccess)
      lines.push(
        `Server-rendered: ${crawl.contentAccess.isServerRendered ? 'yes' : 'no'}`
      )
    sections.push(lines.join('\n'))
  }

  // Robots findings
  if (robots) {
    const issues = robots.syntaxIssues?.length || 0
    sections.push(
      `## Robots.txt: ${robots.overallScore}/100${issues > 0 ? `, ${issues} syntax issue(s)` : ', no syntax issues'}`
    )
  }

  // llms.txt findings
  if (llms) {
    const lines = [
      `## llms.txt: ${llms.llmsTxtFound ? 'found' : 'NOT found'} (${llms.overallScore}/100)`,
    ]
    if (llms.llmsFullTxtFound) lines.push('llms-full.txt also found')
    if (llms.scoreBreakdown) {
      const weak = Object.entries(llms.scoreBreakdown)
        .filter(([, v]) => typeof v === 'number' && (v as number) < 50)
        .map(([k]) => k)
      if (weak.length > 0) lines.push(`Weak areas: ${weak.join(', ')}`)
    }
    sections.push(lines.join('\n'))
  }

  // Readiness findings
  if (readiness) {
    const lines = [`## AI Readiness: ${readiness.overallScore}/100`]
    if (readiness.categoryScores) {
      const weak = Object.entries(readiness.categoryScores)
        .filter(([, v]: [string, any]) => v?.score != null && v.score < 50)
        .map(([k, v]: [string, any]) => `${k}: ${v.score}`)
      if (weak.length > 0) lines.push(`Below 50: ${weak.join(', ')}`)
    }
    sections.push(lines.join('\n'))
  }

  // Citation findings
  if (citation) {
    const providers = citation.providers?.filter((p: any) => p.available) || []
    if (providers.length > 0) {
      const providerLines = providers.map(
        (p: any) =>
          `${p.provider}: ${Math.round((p.overallMentionRate || 0) * 100)}% mention rate`
      )
      const allQueries = providers.flatMap((p: any) => p.queries || [])
      const mentionedCount = allQueries.filter(
        (q: any) => q.brandMentioned
      ).length
      const cautionaryCount = allQueries.filter(
        (q: any) => q.sentimentDetail?.tone === 'cautionary'
      ).length
      const cautionaryPct =
        mentionedCount > 0
          ? Math.round((cautionaryCount / mentionedCount) * 100)
          : 0

      const lines = [
        `## Citations: ${providers.length} provider(s) checked`,
        providerLines.join(', '),
      ]
      if (cautionaryPct > 0)
        lines.push(`Cautionary sentiment: ${cautionaryPct}% of mentions`)
      sections.push(lines.join('\n'))
    }
  }

  // Reddit findings
  if (reddit) {
    const lines = [
      `## Reddit: ${reddit.totalMentions || 0} mention(s), score ${reddit.overallScore}/100`,
    ]
    if (reddit.seedingMatrix) {
      const seeded =
        (reddit.seedingMatrix.likelySeeded || 0) +
        (reddit.seedingMatrix.suspicious || 0)
      if (seeded > 0) lines.push(`Suspicious/seeded: ${seeded}`)
    }
    sections.push(lines.join('\n'))
  }

  // AEO findings
  if (aeo) {
    sections.push(`## AEO Audit: ${aeo.overallScore}/100 (${aeo.grade})`)
  }

  // Answer Engine findings
  if (answer) {
    const lines = hasUsableScore(tools.answer)
      ? [`## Citation Test: ${answer.overallScore}/100 (${answer.grade})`]
      : ['## Citation Test: score withheld']
    const answerSources = readAnswerEngineSources(answer)
    if (answerSources.verifiedCompetitorUrls.length > 0)
      lines.push(
        `Verified competitors cited instead: ${answerSources.verifiedCompetitorUrls.slice(0, 3).join(', ')}`
      )
    else if (answerSources.otherSourceUrls.length > 0)
      lines.push(
        `Other sources cited (not competitors): ${answerSources.otherSourceUrls.slice(0, 3).join(', ')}`
      )
    sections.push(lines.join('\n'))
  }

  // Source Influence
  if (sourceInfluence) {
    const lines = [
      `## Source Influence: ${sourceInfluence.brandInSources ? 'Brand IS a cited source' : 'Brand NOT a cited source'}`,
    ]
    if (
      !sourceInfluence.brandInSources &&
      sourceInfluence.topSources?.length > 0
    ) {
      lines.push(
        `Top sources instead: ${sourceInfluence.topSources
          .slice(0, 3)
          .map((s: any) => s.domain)
          .join(', ')}`
      )
    }
    sections.push(lines.join('\n'))
  }

  // Prompt SOV
  if (promptSov?.targetSOV) {
    if (!hasUsableScore(tools.promptSov)) {
      sections.push('## Share of Voice: score withheld (evaluation incomplete)')
    } else {
      const sov = promptSov.targetSOV
      const hasTarget = (sov.mentionCount || 0) > 0
      sections.push(
        `## Share of Voice: ${hasTarget ? `#${sov.rank}` : 'not ranked'}, ${sov.sovPercent || 0}% SOV, ${promptSov.totalBrandsFound || 0} brands found`
      )
    }
  }

  // Schema Audit
  if (schemaAudit) {
    const lines = [`## Schema Audit: ${schemaAudit.overallScore}/100`]
    lines.push(`Speakable: ${schemaAudit.hasSpeakable ? 'yes' : 'NO'}`)
    if (schemaAudit.schemaTypes) {
      const missing = schemaAudit.schemaTypes
        .filter((t: any) => !t.found)
        .map((t: any) => t.type)
      if (schemaAudit.hasPageTypeSchema === false) {
        missing.push('page type (WebPage/Article/FAQPage/Product/CreativeWork)')
      }
      if (missing.length > 0)
        lines.push(`Missing schema: ${missing.slice(0, 5).join(', ')}`)
    }
    sections.push(lines.join('\n'))
  }

  // Hallucination Check
  if (hallucinationCheck) {
    const withheld =
      hallucinationCheck.scoreStatus === 'withheld' ||
      hallucinationCheck.overallScore == null
    const lines = [
      withheld
        ? '## Hallucination Check: score withheld (evaluation incomplete)'
        : `## Hallucination Check: ${hallucinationCheck.totalInaccuracies || 0} supported inaccuracies`,
    ]
    if (!withheld && hallucinationCheck.bySeverity?.high > 0)
      lines.push(
        `HIGH severity: ${hallucinationCheck.bySeverity.high} inaccuracies`
      )
    const judgedProviders = hallucinationCheck.judgedProviders || []
    if (judgedProviders.length > 0)
      lines.push(`Judged: ${judgedProviders.join(', ')}`)
    else if (!withheld && hallucinationCheck.providersChecked?.length > 0)
      lines.push(`Checked: ${hallucinationCheck.providersChecked.join(', ')}`)
    sections.push(lines.join('\n'))
  }

  // Cross-tool insights (full title + message, not just titles)
  if (insights.length > 0) {
    const insightLines = insights
      .slice(0, 8)
      .map(i => `[${i.type.toUpperCase()}] ${i.title}: ${i.message}`)
    sections.push(`## Key Insights\n${insightLines.join('\n')}`)
  }

  // Top action items
  if (actions.length > 0) {
    const actionLines = actions
      .slice(0, 5)
      .map(
        a =>
          `- ${a.title} (effort: ${a.effort}, impact: ${a.impact}) -- ${a.why}`
      )
    sections.push(`## Top Actions\n${actionLines.join('\n')}`)
  }

  // Cap total length
  let result = sections.join('\n\n')
  if (result.length > MAX_CONTEXT_LENGTH) {
    result = result.slice(0, MAX_CONTEXT_LENGTH - 3) + '...'
  }
  return result
}

/**
 * Generate dynamic suggested questions based on actual audit findings.
 * Pure function, no LLM call. Returns 3-5 questions.
 */
export function generateSuggestedQuestions(
  tools: Record<ToolKey, ToolState>,
  insights: Insight[]
): string[] {
  const questions: string[] = []

  const crawl = tools.crawl?.data
  const llms = tools.llms?.data
  const citation = tools.citation?.data
  const reddit = tools.reddit?.data
  const schemaAudit = tools.schemaAudit?.data
  const hallucinationCheck = tools.hallucinationCheck?.data
  const promptSov = tools.promptSov?.data
  const sourceInfluence = tools.sourceInfluence?.data
  const aeo = tools.aeo?.data
  const answer = tools.answer?.data

  // Priority order: most impactful findings first
  if (crawl) {
    const blockedAi =
      crawl.botAccess?.filter(
        (b: any) => !b.allowed && isCrawlRetrievalBot(b.bot)
      ) || []
    if (blockedAi.length > 0)
      questions.push(
        'Which AI bots are blocked on my site and what is the impact?'
      )
  }

  if (
    hallucinationCheck?.scoreStatus !== 'withheld' &&
    typeof hallucinationCheck?.overallScore === 'number' &&
    hallucinationCheck.bySeverity?.high > 0
  )
    questions.push('How do I fix AI hallucinations about my brand?')

  if (llms && !llms.llmsTxtFound)
    questions.push('How should I create and structure my llms.txt file?')

  if (citation) {
    const rates =
      citation.providers
        ?.filter((p: any) => p.available)
        .map((p: any) => p.overallMentionRate) || []
    const avg =
      rates.length > 0
        ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length
        : 0
    if (avg < 0.3) questions.push('Why are AI platforms not citing my brand?')

    // Cautionary sentiment
    const allQueries =
      citation.providers
        ?.filter((p: any) => p.available)
        .flatMap((p: any) => p.queries || []) || []
    const mentionedCount = allQueries.filter(
      (q: any) => q.brandMentioned
    ).length
    const cautionaryCount = allQueries.filter(
      (q: any) => q.sentimentDetail?.tone === 'cautionary'
    ).length
    if (mentionedCount > 0 && cautionaryCount / mentionedCount >= 0.3)
      questions.push('Why are AI platforms describing my brand with caveats?')
  }

  if (schemaAudit && !schemaAudit.hasSpeakable)
    questions.push('What schema markup do I need for AI visibility?')

  if (
    hasUsableScoreData(promptSov) &&
    (promptSov.targetSOV?.mentionCount || 0) === 0
  )
    questions.push('How do I appear in AI category recommendations?')

  if (sourceInfluence && !sourceInfluence.brandInSources)
    questions.push('How do I become a cited source in my category?')

  if (reddit?.totalMentions === 0)
    questions.push('How does Reddit activity affect AI citations?')

  if (aeo && aeo.overallScore < 40)
    questions.push('How do I optimize my content for answer engines?')

  if (
    answer &&
    readAnswerEngineSources(answer).verifiedCompetitorUrls.length > 0
  )
    questions.push('Why are competitors being cited instead of me?')

  // Check insights for conflicts (high-value conversation starters)
  const hasConflict = insights.some(i => i.type === 'conflict')
  if (hasConflict && questions.length < 5)
    questions.push('What are the biggest conflicts in my AI visibility?')

  // Fallback if fewer than 3 tool-specific questions
  const fallbacks = [
    'How do I improve my overall AI visibility score?',
    'What is GEO and how does it apply to my site?',
    'What should I prioritize first based on my audit?',
  ]
  for (const fb of fallbacks) {
    if (questions.length >= 5) break
    if (!questions.includes(fb)) questions.push(fb)
  }

  return questions.slice(0, 5)
}

/* eslint-enable @typescript-eslint/no-explicit-any */
